import { CIActionBtnGroup, Checkbox, Dropdown, Input } from 'ccs-ui-components';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { isEmojiInput } from '../../../../../../common/utils/CommonUtils';
import { CONTENT_TYPE_OPTIONS, MODE, OPERATOR_OPTIONS, PATTERN_FORM_MAXLENGTH } from '../../../../../constants';
import { IFieldDefRowItem, IFieldDefinitionFormProps, IPatternRegistryValidationModel, PatternRegistryFormData } from '../../../../../models';
import { clearFormDataDynamic, conversionPatternChange, duplicateNameChecker, enableAdd, enableClear, fieldDefClickHandler, isPatternEditMode, syncState } from '../../../../../utilities/sources/PatternRegistryUtil';
import classes from '../PatternRegistryForm.module.scss';

const withinConversionPatternChangeHandler = (e: React.ChangeEvent<HTMLInputElement>, data: IFieldDefRowItem[], formData: IPatternRegistryValidationModel, setFormParamValues: Dispatch<SetStateAction<PatternRegistryFormData>>, setDuplicateError: Dispatch<SetStateAction<string | undefined>>) => {
  if (isEmojiInput(e.target.value)) {
    return;
  }
  const duplicateConversionPatternValue = data.filter((item) => item.conversionPattern === e.target.value);
  conversionPatternChange(duplicateConversionPatternValue, formData, setFormParamValues, setDuplicateError);
};

const FieldDefinitionComponent = (props: IFieldDefinitionFormProps) => {
  const { formData, handleInputChange, setFormParamValues, updateList, data, fieldEnabler, isEditFD, isEditPatternMode, isViewPatternMode, setEditHandler, setShowFieldDefInput, showFieldDefInput, setCurrentEditId, currentEditId } = props;

  const { t: translate } = useTranslation();

  const [operator, setOperator] = useState('');
  const [contentType, setContentType] = useState('');
  const [name, setName] = useState('');
  const [conversionPattern, setConversionPattern] = useState('');
  const [duplicateError, setDuplicateError] = useState<string | undefined>('');
  const [isReqd, setIsReqd] = useState<boolean>(false);

  /**
   * @method conversionPatternChangeHandler
   * @description Handles the change event for the conversion pattern input field.
   * If the input value is an emoji, it does nothing.
   * Otherwise, it checks for duplicate conversion pattern values and updates the form data accordingly.
   * Finally, it updates the conversion pattern state and triggers the input change handler.
   *
   * @param e - The change event object.
   */
  const conversionPatternChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    withinConversionPatternChangeHandler(e, data, formData, setFormParamValues, setDuplicateError);
    handleInputChange(e);
    setConversionPattern(e.target.value);
  };

  /**
   * @method operatorChangeHandler
   * @description Handles the change event of the operator select element.
   * Updates the operator value in the form data and triggers a re-render.
   * @param e - The change event object.
   */
  const operatorChangeHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setOperator(e.target.value);
    const newFormData = { ...formData };
    newFormData.fieldDefOperator.value = e.target.value;
    setFormParamValues(newFormData);
  };

  /**
   * @method contentTypeChangeHandler
   * @description Handles the change event of the content type select element.
   * Updates the content type value in the form data and sets the new form parameter values.
   * @param e - The change event object.
   */
  const contentTypeChangeHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setContentType(e.target.value);
    const newFormData = { ...formData };
    newFormData.fieldDefContentType.value = e.target.value;
    setFormParamValues(newFormData);
  };

  /**
   * @method nameChangeHandler
   * @description Handles the change event for the name input field.
   * Checks if the input value contains emojis and returns early if it does.
   * Filters the data array to find any duplicate names and sets the duplicate error if found.
   * Updates the form parameter values and name state.
   * @param e - The change event object.
   */
  const nameChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isEmojiInput(e.target.value)) {
      return;
    }
    const duplicateNameValue = data.filter((item) => item.name === e.target.value);
    const errorString = String(translate('fieldDefCompForm.fieldNameAlreadyExists'));
    duplicateNameChecker(duplicateNameValue, formData, setDuplicateError, setFormParamValues, errorString);
    handleInputChange(e);
    setName(e.target.value);
  };

  /**
   * @method checkDuplicateName
   * @description checks for duplicate names in form and existing list items
   * @returns boolean
   */
  const checkDuplicateName = () => {
    const hasDuplicate = data.find((item) => item.name === formData.fieldDefName.value);
    return hasDuplicate === undefined;
  };

  /**
   * @method clickHandler
   * @description Handles the click event for the field definition form.
   * Updates the field definition list based on the operation (ADD or EDIT).
   * Clears the form input fields and hides the field definition form.
   */
  const clickHandler = () => {
    fieldDefClickHandler(isEditFD, isEditPatternMode, formData);
    const fieldDefObj = {
      required: formData.fieldDefIsRequired.value,
      id: `fd-item-${Date.now()}-${formData.fieldDefName.value}`,
      name: formData.fieldDefName.value,
      operator: formData.fieldDefOperator.value,
      value: formData.fieldDefValue.value,
      contentType: formData.fieldDefContentType.value,
      conversionPattern: formData.fieldDefConversionPattern.value
    };

    // check for ADD operation
    if (checkDuplicateName()) {
      setOperator('');
      setContentType('');
      setName('');
      setConversionPattern('');
      setIsReqd(false);
      updateList(fieldDefObj, MODE.ADD);
    }
    if (isEditFD) { // check for EDIT operation
      setOperator('');
      setContentType('');
      setName('');
      setConversionPattern('');
      setIsReqd(false);
      updateList(fieldDefObj, MODE.EDIT);
    }
    setShowFieldDefInput(false);
  };

  /**
   * @method clearFormData
   * @description Clears the form data and resets the state variables.
   */
  const clearFormData = () => {
    const newFormData = { ...formData };
    clearFormDataDynamic(newFormData, [formData.fieldDefName.name, formData.fieldDefOperator.name, formData.fieldDefValue.name, formData.fieldDefContentType.name, formData.fieldDefConversionPattern.name]);
    formData.fieldDefIsRequired.value = false;
    setFormParamValues(newFormData);
    setOperator('');
    setContentType('');
    setName('');
    setConversionPattern('');
    setDuplicateError('');
    if (isEditFD) {
      setEditHandler(false);
    }
    fieldEnabler(false);
    if (currentEditId) setShowFieldDefInput(false);
    setCurrentEditId(undefined);
  };

  /**
   * @method handleSelect
   * @description Handles the selection of a field definition.
   */
  const handleSelect = () => {
    formData.fieldDefIsRequired.isTouched = true;
    formData.fieldDefIsRequired.value = !isReqd;
    setIsReqd(!isReqd);
    setFormParamValues(formData);
  };

  /**
   * when we edit a data on field definition list and then delete it, the list gets updated and the duplicate error will be cleared.
   */
  useEffect(() => {
    if (data.length && checkDuplicateName()) {
      const newFormData = { ...formData };
      newFormData.fieldDefName.isValid = true;
      setDuplicateError('');
    }
  }, [data]);

  /**
   * useEffect to sync local state when formData is updated externally
   * Note: use only for syncing
   */
  useEffect(() => {
    syncState({ operator, contentType, name, conversionPattern, isReqd, setOperator, setContentType, setName, setConversionPattern, setIsReqd, formData });
  }, [formData]);

  return (
    ((isPatternEditMode(isViewPatternMode, isEditPatternMode)) ? (
      <>
        { }
        {showFieldDefInput && (
          <Form className={classes['pr-field-def-frm-container']}>
            <Row>
              <Col sm={10}>
                <Row>
                  <Col sm={1}>
                    <Checkbox
                      onClick={() => handleSelect()}
                      checked={isReqd}
                      data-testid="check-reqd"
                    />
                  </Col>
                  <Col sm={2}>
                    <Form.Group
                      className={classes['form-grp']}
                      controlId="formPatternRegistryName"
                    >
                      <Input
                        type="text"
                        placeholder={String(translate('pattern_registry_form.plchd_fd_name'))}
                        label=""
                        name="fieldDefName"
                        value={name}
                        onChange={nameChangeHandler}
                        error={!formData.fieldDefName.isValid || duplicateError !== ''}
                        data-testid="pr-fieldDefName"
                        maxLength={Number(PATTERN_FORM_MAXLENGTH.fieldDefNameLength)}
                        isMandatory
                      />
                      <div className={classes['cp-pr-error-container']}>
                        {!formData.fieldDefName.isValid
                          && formData.fieldDefName.isTouched && (
                            <p className={classes['cp-pr-error']} data-testid="pattern-name-error-text">
                              {formData.fieldDefName.error}
                            </p>
                          )}
                        {data.length > 0 && duplicateError !== '' && (
                          <p className={classes['cp-pr-error']} data-testid="name-duplicate-error">
                            {duplicateError}
                          </p>
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                  <Col sm={3}>
                    <Form.Group
                      className={classes['form-grp']}
                      controlId="formPatternRegistryName"
                    >
                      <Dropdown
                        label=""
                        data={OPERATOR_OPTIONS}
                        value={operator}
                        onChange={operatorChangeHandler}
                        name="fieldDefOperator"
                        data-testid="fieldDefOperatorTest"
                        error={!formData.fieldDefOperator.isValid}
                        placeholder={String(translate('fsm_registry_form.lbl_placeholder_select'))}
                      />
                      <div className={classes['cp-pr-error-container']}>
                        {!formData.fieldDefOperator.isValid
                          && formData.fieldDefOperator.isTouched && (
                            <h6 className={classes['cp-pr-error']} data-testid="pattern-name-error-text">
                              {formData.fieldDefOperator.error}
                            </h6>
                          )}
                      </div>
                    </Form.Group>
                  </Col>
                  <Col sm={2}>
                    <Form.Group
                      className={classes['form-grp']}
                      controlId="formPatternRegistryName"
                    >
                      <Input
                        type="text"
                        placeholder={String(translate('pattern_registry_form.plchd_fd_value'))}
                        label=""
                        name="fieldDefValue"
                        value={formData.fieldDefValue.value}
                        onChange={handleInputChange}
                        error={!formData.fieldDefValue.isValid}
                        data-testid="pr-fieldDefValue"
                      />
                      <div className={classes['cp-pr-error-container']}>
                        {!formData.fieldDefValue.isValid
                          && formData.fieldDefValue.isTouched && (
                            <h6 className={classes['cp-pr-error']} data-testid="pattern-name-error-text">
                              {formData.fieldDefValue.error}
                            </h6>
                          )}
                      </div>
                    </Form.Group>
                  </Col>
                  <Col sm={2}>
                    <Form.Group
                      className={classes['form-grp']}
                      controlId="formPatternRegistryName"
                    >
                      <Dropdown
                        label=""
                        data={CONTENT_TYPE_OPTIONS}
                        value={contentType}
                        onChange={contentTypeChangeHandler}
                        name="fieldDefContentType"
                        data-testid="fieldDefContentTypeTest"
                        error={!formData.fieldDefContentType.isValid}
                        placeholder={String(translate('fsm_registry_form.lbl_placeholder_select'))}
                      />
                      <div className={classes['cp-pr-error-container']}>
                        {!formData.fieldDefContentType.isValid
                          && formData.fieldDefContentType.isTouched && (
                            <h6 className={classes['cp-pr-error']} data-testid="pattern-name-error-text">
                              {formData.fieldDefContentType.error}
                            </h6>
                          )}
                      </div>
                    </Form.Group>
                  </Col>
                  <Col sm={2}>
                    <Form.Group
                      className={classes['form-grp']}
                      controlId="formPatternRegistryName"
                    >
                      <Input
                        type="text"
                        placeholder={String(translate('pattern_registry_form.plchd_cp_value'))}
                        label=""
                        className={classes['convPattern-input']}
                        name="fieldDefConversionPattern"
                        value={conversionPattern}
                        onChange={conversionPatternChangeHandler}
                        error={!formData.fieldDefConversionPattern.isValid}
                        data-testid="pr-fieldDefConversionPattern"
                      />
                      <div className={classes['cp-pr-error-container']}>
                        {!formData.fieldDefConversionPattern.isValid
                          && formData.fieldDefConversionPattern.isTouched && (
                            <h6 className={classes['cp-pr-error']} data-testid="pattern-name-error-text">
                              {formData.fieldDefConversionPattern.error}
                            </h6>
                          )}
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </Col>
              <Col sm={2}>
                <div className={classes['btn-icon-contnr']}>
                  <CIActionBtnGroup
                    buttons={[
                      { label: 'check', key: 'checkIcon', onClick: () => clickHandler(), testId: 'pr-fd-check-btn', disabled: enableAdd(formData, duplicateError) },
                      { label: 'cross', key: 'xMarkSmallIcon', onClick: () => clearFormData(), testId: 'pr-fd-cross-btn', disabled: enableClear(formData) }
                    ]}
                  />
                </div>
              </Col>
            </Row>
          </Form>
        )}
      </>
    ) : null)
  );
};
export default FieldDefinitionComponent;
